'use client';
import React from 'react';
import { OrderDetails as Shared } from '@launchstore/shared-puck';
import { useOrderData } from '@/lib/hooks/useOrderData';
import { resolvePaymentMeta } from '@launchstore/shared-puck';
import { convertToLocale } from '@lib/util/money';

const SharedRender = (Shared as any).render;

const mapAddress = (a: any) =>
  a
    ? {
        first_name: a.first_name ?? '',
        last_name: a.last_name ?? '',
        address_1: a.address_1 ?? '',
        city: a.city ?? '',
        province: a.province ?? '',
        postal_code: a.postal_code ?? '',
        country: (a.country_code ?? '').toUpperCase(),
      }
    : undefined;

/**
 * Storefront OrderDetails — binds the real Medusa order (from Puck
 * `context.order` or `useOrderData()`) into the shared presentational
 * component. Maps the order's payment record (from payment_collections)
 * to the shared `OrderPayment` shape (provider friendly name, card
 * last 4, card brand). The friendly name comes from the shared
 * PAYMENT_INFO_MAP keyed by Medusa provider id.
 */
function OrderDetailsImpl(props: any) {
  const { order, currencyCode } = useOrderData();

  if (!order) {
    return <SharedRender {...props} />;
  }

  const currency = order.currency_code ?? currencyCode ?? 'usd';
  const fmt = (amount: number) =>
    convertToLocale({ amount: Number(amount) || 0, currency_code: currency });

  const items = (order.items ?? []).map((i: any) => ({
    id: i.id,
    title: i.title ?? i.product_title ?? '',
    variant: i.variant?.title ?? i.variant_title ?? '',
    thumbnail: i.thumbnail,
    quantity: i.quantity ?? 1,
    unit_price: Number(i.unit_price) || 0,
    total: Number(i.total ?? (Number(i.unit_price) || 0) * (i.quantity ?? 1)) || 0,
  }));

  const shippingMethod = order.shipping_methods?.[0]
    ? {
        method: order.shipping_methods[0].name ?? 'Shipping',
        cost: Number(order.shipping_methods[0].amount) || 0,
      }
    : undefined;

  // Payment method: from order.payment_collections[0].payments[0]
  const paymentRecord = order.payment_collections?.[0]?.payments?.[0];
  const payment = paymentRecord
    ? (() => {
        const meta = resolvePaymentMeta(paymentRecord.provider_id ?? '');
        return {
          provider: meta.title,
          card_brand: paymentRecord.data?.card_brand ?? '',
          card_last4: paymentRecord.data?.card_last4 ?? '',
        };
      })()
    : undefined;

  const injected = {
    items,
    shippingAddress: mapAddress(order.shipping_address),
    billingAddress: mapAddress(order.billing_address),
    shippingMethod,
    payment,
    totals: {
      // Items-only subtotal. In Medusa v2 `order.subtotal` already
      // includes the shipping amount (item_subtotal + shipping_subtotal,
      // pre-tax), so using it made "Subtotal" equal "Total" while
      // Shipping was also listed separately (double-counted appearance).
      // Derive from the line items (sum of item.total) instead, matching
      // the per-line prices shown above, so:
      // Subtotal + Shipping + Tax − Discount = Total.
      subtotal:
        Number(order.item_total) ||
        Number(order.item_subtotal) ||
        items.reduce((s: number, i: any) => s + (Number(i.total) || 0), 0),
      shipping_total: Number(order.shipping_total) || 0,
      tax_total: Number(order.tax_total) || 0,
      total: Number(order.total) || 0,
    },
    formatPrice: fmt,
  };

  return <SharedRender {...props} {...injected} />;
}

export const OrderDetails: any = {
  ...Shared,
  render: (props: any) => <OrderDetailsImpl {...props} />,
};
export default OrderDetails;
